import React, { FunctionComponent } from 'react'

import {
  StyledCastorCard,
  StyledCastorCardButton,
  StyledCastorCardContent,
  StyledCastorCardImage
} from './CastorActionCard.styles'
import { ClinTheme } from '../../ClinTheme'
import { ClinSpacer } from '../../components/ClinSpacer'
import { ClinText } from '../../components/ClinText'
import { useAppContext } from '../../context/app'
import {
  mapToPatientOrderStatus,
  PatientOrderStatus
} from '../../pages/Patients/PatientDashboard/Patient.model'
import {
  getCastorEConsentUrl,
  openCastorEdcUrl,
  openExternalUrl
} from '../../utils/rwdFollowUpEligibility'

const FOLLOW_UP_BODY_COPY =
  'As part of a Post-trial Access Program we need to collect data about your patients progress.'

const shippedOrDeliveredStatuses = [
  PatientOrderStatus.Shipped,
  PatientOrderStatus.InTransit,
  PatientOrderStatus.OutForDelivery,
  PatientOrderStatus.Delivered,
  PatientOrderStatus.ReadyForResupply,
  PatientOrderStatus.FailedAttempt,
  PatientOrderStatus.FailedDelivery,
  PatientOrderStatus.Available_for_Pickup
]

export interface ICastorActionCardProps {
  /** The current patient order status, used to determine which CTA to show */
  patientOrderStatus?: string
  /** Deep-link URL for Castor eConsent (used when awaiting consent) */
  castorEConsentUrl?: string
  /** Deep-link URL for Castor EDC (used when awaiting baseline data or for follow-up) */
  castorEdcUrl?: string
  /** Keep consent and baseline actions on the dedicated patient details tab. */
  followUpOnly?: boolean
  /**
   * The patient has had an order shipped and is not discontinued, whatever the
   * current status is. Keeps the follow-up action while a later order has not
   * shipped yet.
   */
  canSubmitFollowUp?: boolean
}

export const CastorActionCard: FunctionComponent<ICastorActionCardProps> = ({
  patientOrderStatus,
  castorEConsentUrl,
  castorEdcUrl,
  followUpOnly = false,
  canSubmitFollowUp = false
}) => {
  const { user, userDetails } = useAppContext()
  const normalizedOrderStatus = mapToPatientOrderStatus(
    patientOrderStatus ?? ''
  ) as PatientOrderStatus

  const isAwaitingConsent =
    !followUpOnly &&
    normalizedOrderStatus === PatientOrderStatus.AwaitingConsent

  // CLOS-14017 / CLOS-13425: always show the baseline CTA when awaiting
  // baseline. Deep-link when present; otherwise fall back to Castor EDC homepage
  // (same pattern as consent and follow-up CTAs).
  const isAwaitingBaseline =
    !followUpOnly &&
    normalizedOrderStatus === PatientOrderStatus.AwaitingBaselineData

  const isEligibleForFollowUp =
    canSubmitFollowUp ||
    (patientOrderStatus !== undefined &&
      shippedOrDeliveredStatuses.includes(normalizedOrderStatus))

  const handleOpenConsent = () => {
    openExternalUrl(getCastorEConsentUrl(castorEConsentUrl))
  }

  const handleOpenEdc = () => {
    openCastorEdcUrl(
      castorEdcUrl,
      user?.profile.email ?? userDetails?.contactCard.email
    )
  }

  if (!isAwaitingConsent && !isAwaitingBaseline && !isEligibleForFollowUp) {
    return null
  }

  return (
    <StyledCastorCard>
      <StyledCastorCardImage />
      <StyledCastorCardContent>
        {isEligibleForFollowUp && (
          <>
            <ClinText
              fontSize={ClinTheme.fontSizes[1]}
              color={ClinTheme.colors.black}
              textAlign="left"
              lineHeight="24px"
              letterSpacing="normal"
              width="100%"
            >
              {/* TODO: replace with i18n key once delivered via Squidex */}
              {FOLLOW_UP_BODY_COPY}
            </ClinText>

            <ClinSpacer height={ClinTheme.space[1]} />
          </>
        )}

        {isAwaitingConsent && (
          <StyledCastorCardButton
            variant="secondary"
            onClick={handleOpenConsent}
          >
            {/* TODO: replace with i18n key once delivered via Squidex */}
            Provide patient consent
          </StyledCastorCardButton>
        )}

        {isAwaitingBaseline && (
          <StyledCastorCardButton variant="secondary" onClick={handleOpenEdc}>
            {/* TODO: replace with i18n key once delivered via Squidex */}
            Submit screening data
          </StyledCastorCardButton>
        )}

        {isEligibleForFollowUp && (
          <StyledCastorCardButton variant="primary" onClick={handleOpenEdc}>
            {/* TODO: replace with i18n key once delivered via Squidex */}
            Submit follow up data
          </StyledCastorCardButton>
        )}
      </StyledCastorCardContent>
    </StyledCastorCard>
  )
}
